/**
 * Formats filter values for display in the UI.
 * This ensures that filter values are displayed with proper casing,
 * even if they were normalized to lowercase during URL parsing.
 */

/**
 * Formats a filter value for display.
 * For known attributes with fixed values (like remoteness), this will format to proper case.
 * For other attributes, it will attempt to format the value nicely (capitalize first letter, etc.).
 * 
 * @param attribute The filter attribute name (e.g., 'remoteness', 'location')
 * @param value The filter value to format
 * @returns A formatted label suitable for display
 */
export function formatFilterLabel(attribute: string, value: string): string {
  if (!value) return value;
  
  const attrLower = attribute.toLowerCase();
  
  // For remoteness, we know the exact values and their proper casing
  if (attrLower === 'remoteness') {
    const remotenessMap: Record<string, string> = {
      'remote': 'Remote',
      'hybrid': 'Hybrid',
      'onsite': 'Onsite',
      'etätyö': 'Etätyö',
      'hybridityö': 'Hybridityö',
      'paikkasidonnainen työ': 'Paikkasidonnainen työ',
      'distans': 'Distans',
      'på plats': 'På plats',
    };
    
    const normalized = remotenessMap[value.toLowerCase()];
    if (normalized) {
      return normalized;
    }
  }
  
  // For other attributes, try to format nicely
  // Capitalize first letter of each word, but preserve special characters
  const words = value.split(/\s+/);
  const formatted = words
    .map(word => {
      // Don't capitalize if it's all uppercase (likely an acronym)
      if (word === word.toUpperCase() && word.length > 1) {
        return word;
      }
      // Capitalize first letter, preserve rest
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
  
  return formatted;
}

/**
 * Formats a filter attribute name for display.
 * Converts camelCase or lowercase attribute names to a readable format.
 * 
 * @param attribute The filter attribute name (e.g., 'remoteness', 'companyIndustry')
 * @returns A formatted attribute name suitable for display
 */
export function formatFilterAttributeName(attribute: string): string {
  if (!attribute) return attribute;
  
  // Remove 'exclude_' prefix if present
  const cleanAttribute = attribute.replace(/^exclude_/, '');
  
  // Convert camelCase to Title Case
  const formatted = cleanAttribute
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
    .trim();
  
  return formatted;
}
